import { useState, useEffect, useCallback } from 'react';

/**
 * Hook para gestionar la lista de colaboradores/participantes de un ticket,
 * auto-asignación de titular inicial y registro automático al interactuar en el chat.
 */
export const useTicketParticipants = ({
  activeTicketId,
  activeTicket,
  token,
  idUsuario,
  nombreCompleto,
  setTicketDetail,
  onTicketUpdated
}) => {
  const [participants, setParticipants] = useState([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);

  // 1. Cargar participantes actuales del ticket
  const fetchParticipants = useCallback(async (optimisticParticipant) => {
    if (optimisticParticipant) {
      setParticipants(prev => {
        if (prev.some(p => Number(p.idUsuario) === Number(optimisticParticipant.idUsuario))) return prev;
        return [...prev, optimisticParticipant];
      });
    }

    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    setIsLoadingParticipants(true);
    try {
      const resp = await fetch(`/api/SupportEngine/GetTicketParticipants?idTicket=${activeTicketId}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (resp.ok) {
        const result = await resp.json();
        const data = result.data || (Array.isArray(result) ? result : []);
        setParticipants(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al consultar participantes:', err);
    } finally {
      setIsLoadingParticipants(false);
    }
  }, [activeTicketId, token]);

  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  // 2. Asignar responsable inicial si el ticket no tenía titular
  const assignInitialResponsible = useCallback(async (ticketId, userId) => {
    try {
      const resp = await fetch('/api/SupportEngine/AssignTicketAgent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idUsuarioAsignado: userId
        })
      });
      return resp.ok;
    } catch (err) {
      console.error('Error al auto-asignar responsable inicial:', err);
      return false;
    }
  }, [token]);

  // 3. Registrar nuevo participante/colaborador
  const addTicketParticipant = useCallback(async (ticketId, userId) => {
    try {
      const resp = await fetch('/api/SupportEngine/AddTicketParticipant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idUsuario: userId
        })
      });
      return resp.ok;
    } catch (err) {
      console.error('Error al registrar participante:', err);
      return false;
    }
  }, [token]);

  // 4. Sincronizar asignación o colaboración después de emitir un mensaje
  const syncTicketAssignmentAfterMessage = useCallback(async () => {
    const currentId = activeTicketId;
    if (!currentId || !idUsuario) return;

    const numericCurrentAssignee = Number(activeTicket?.idUsuarioAsignado);
    const numericCurrentUserId = Number(idUsuario);

    // Si el ticket no tiene responsable asignado aún
    if (!numericCurrentAssignee || numericCurrentAssignee === 0) {
      const updated = await assignInitialResponsible(currentId, numericCurrentUserId);
      if (updated) {
        setTicketDetail(prev => ({
          ...prev,
          idUsuarioAsignado: numericCurrentUserId,
          vNombreUsuarioAsignado: nombreCompleto
        }));
        if (onTicketUpdated) onTicketUpdated();
      }
      return;
    }

    // Si el usuario actual ya es el responsable asignado
    if (numericCurrentAssignee === numericCurrentUserId) {
      return;
    }

    // Si el usuario actual ya figura como participante del ticket
    const isAlreadyParticipant = participants.some(p => Number(p.idUsuario) === numericCurrentUserId);
    if (isAlreadyParticipant) {
      return;
    }

    // Solo si no es responsable y no está en la lista de participantes, se agrega
    const added = await addTicketParticipant(currentId, numericCurrentUserId);
    if (added) {
      setParticipants(prev => {
        if (prev.some(p => Number(p.idUsuario) === numericCurrentUserId)) return prev;
        return [
          ...prev,
          {
            idTicketParticipante: Date.now(),
            idUsuario: numericCurrentUserId,
            vNombreCompleto: nombreCompleto,
            dCreatedDate: new Date().toISOString()
          }
        ];
      });
      await fetchParticipants();
    }
  }, [
    activeTicketId,
    idUsuario,
    activeTicket?.idUsuarioAsignado,
    assignInitialResponsible,
    nombreCompleto,
    setTicketDetail,
    onTicketUpdated,
    participants,
    addTicketParticipant,
    fetchParticipants
  ]);

  return {
    participants,
    setParticipants,
    isLoadingParticipants,
    fetchParticipants,
    assignInitialResponsible,
    addTicketParticipant,
    syncTicketAssignmentAfterMessage
  };
};
